import * as React from 'react';

import {
  AreaRecorte,
  nomeDeSaida,
  normalizarRecorte,
  tipoDeSaida,
} from './imageCrop';

const QUALIDADE_JPEG = 0.92;

const carregarImagem = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const imagem = new Image();

    imagem.onload = () => resolve(imagem);
    imagem.onerror = () => reject(new Error('Não foi possível ler a imagem.'));
    imagem.src = url;
  });

const paraBlob = (canvas: HTMLCanvasElement, tipo: string): Promise<Blob> =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) =>
        blob ? resolve(blob) : reject(new Error('Não foi possível gerar a imagem recortada.')),
      tipo,
      QUALIDADE_JPEG,
    );
  });

export const recortarArquivo = async (
  arquivo: File,
  area: AreaRecorte,
): Promise<File> => {
  const url = URL.createObjectURL(arquivo);

  try {
    const imagem = await carregarImagem(url);
    const recorte = normalizarRecorte(area, imagem.naturalWidth, imagem.naturalHeight);

    const canvas = document.createElement('canvas');
    canvas.width = recorte.width;
    canvas.height = recorte.height;

    const contexto = canvas.getContext('2d');
    if (!contexto) throw new Error('Não foi possível preparar o recorte.');

    contexto.drawImage(
      imagem,
      recorte.x,
      recorte.y,
      recorte.width,
      recorte.height,
      0,
      0,
      recorte.width,
      recorte.height,
    );

    const tipo = tipoDeSaida(arquivo.type);
    const blob = await paraBlob(canvas, tipo);

    return new File([blob], nomeDeSaida(arquivo.name, tipo), {
      type: tipo,
      lastModified: Date.now(),
    });
  } finally {
    URL.revokeObjectURL(url);
  }
};

interface Entrada {
  arquivo: File;
  onConfirm: (arquivo: File) => void;
}

export const useImageCropModal = ({arquivo, onConfirm}: Entrada) => {
  const [posicao, setPosicao] = React.useState({x: 0, y: 0});
  const [zoom, setZoom] = React.useState(1);
  const [area, setArea] = React.useState<AreaRecorte | null>(null);
  const [processando, setProcessando] = React.useState(false);
  const [erro, setErro] = React.useState<string | null>(null);

  const urlOrigem = React.useMemo(() => URL.createObjectURL(arquivo), [arquivo]);

  React.useEffect(() => () => URL.revokeObjectURL(urlOrigem), [urlOrigem]);

  // O react-easy-crop e de proporcao fixa e cai em 4/3 quando nenhuma e
  // informada — o que cortaria uma capa 16/9 sem o usuario pedir. Sem proporcao
  // exigida, o recorte comeca sendo a imagem inteira.
  const [aspectoNatural, setAspectoNatural] = React.useState<number | null>(null);

  React.useEffect(() => {
    let vivo = true;

    carregarImagem(urlOrigem)
      .then((imagem) => {
        if (vivo && imagem.naturalHeight > 0) {
          setAspectoNatural(imagem.naturalWidth / imagem.naturalHeight);
        }
      })
      .catch(() => {
        if (vivo) setErro('Não foi possível ler a imagem.');
      });

    return () => {
      vivo = false;
    };
  }, [urlOrigem]);

  const aoTerminarRecorte = React.useCallback(
    (_areaVisivel: AreaRecorte, areaEmPixels: AreaRecorte) => setArea(areaEmPixels),
    [],
  );

  const confirmar = React.useCallback(async () => {
    if (!area || processando) return;

    setProcessando(true);
    setErro(null);

    try {
      onConfirm(await recortarArquivo(arquivo, area));
    } catch (falha) {
      setErro((falha as Error).message || 'Não foi possível recortar a imagem.');
      setProcessando(false);
    }
  }, [area, arquivo, onConfirm, processando]);

  return {
    urlOrigem,
    aspectoNatural,
    posicao,
    setPosicao,
    zoom,
    setZoom,
    aoTerminarRecorte,
    confirmar,
    processando,
    erro,
    podeConfirmar: Boolean(area) && !processando,
  };
};
